import { supportedCountryCodes } from 'jigsaw';
import { useSettings } from '@src/data/settings';
import { countries, getEmojiFlag, TCountryCode } from 'countries-list';
import { useMemo, useState } from 'react';
import { BiCheck, BiSearch } from 'react-icons/bi';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  HStack,
  Input,
  RadioGroup,
  RadioGroupPrimitive,
  Text,
  VStack,
} from 'ui_2';
import { useDebounce } from 'use-debounce';
import { Link } from '@tanstack/react-router';
import { Route as country_select_route } from '@routes/countrySelect';
import { useViewTransition } from '@src/hooks/useViewTransition';

const CountryItem = ({
  countryCode,
  tabIndex,
}: {
  countryCode: TCountryCode | 'global';
  tabIndex: number;
}) => {
  return (
    <RadioGroupPrimitive.Item
      value={countryCode}
      className={`group py-2`}
      tabIndex={tabIndex}
      role='radio'
    >
      <HStack className='w-full items-center gap-2'>
        <Text>
          {countryCode === 'global' ? '🌎' : getEmojiFlag(countryCode)}
        </Text>
        <Text>
          {countryCode === 'global'
            ? 'All countries'
            : countries[countryCode].name}
        </Text>
        <HStack className='flex-grow' />
        <div className='relative flex items-center justify-end pr-4'>
          <BiCheck className='absolute text-green-600 opacity-0 group-hover:opacity-100' />
          <RadioGroupPrimitive.Indicator className='absolute'>
            <BiCheck className='text-green-800' />
          </RadioGroupPrimitive.Indicator>
        </div>
      </HStack>
    </RadioGroupPrimitive.Item>
  );
};

const CountrySelectorTrigger = () => {
  const [_countryCode] = useSettings((state) => [state.countryCode]);

  const countryCode = _countryCode ?? 'global';

  const labelEmoji =
    countryCode === 'global' ? '🌎' : getEmojiFlag(countryCode);
  const labelText = countryCode === 'global' ? 'All' : countryCode;

  return (
    <HStack className='h-fit cursor-pointer items-center gap-2 rounded-full border border-gray-100 px-4 py-2 transition-colors hover:border-gray-200  active:bg-gray-50'>
      <Text>{labelEmoji}</Text>
      <Text>{labelText}</Text>
    </HStack>
  );
};

const CountrySelectorContent = () => {
  const [_countryCode, setCountryCode] = useSettings((state) => [
    state.countryCode,
    state.setCountryCode,
  ]);
  const countryCode = _countryCode ?? 'global';

  const [search, setSearch] = useState<string>();

  const [debouncedSearch] = useDebounce(search, 250);

  const filteredCountryCodes: (TCountryCode | 'global')[] = useMemo(() => {
    if (debouncedSearch === undefined || debouncedSearch.trim() === '')
      return ['global', ...supportedCountryCodes];
    const formattedDebouncedSearch = debouncedSearch.trim().toLowerCase();

    return supportedCountryCodes.filter((cc) => {
      const country = countries[cc];
      return (
        country.name.toLowerCase().includes(formattedDebouncedSearch) ||
        cc.toLowerCase().includes(formattedDebouncedSearch) ||
        country.native.toLowerCase().includes(formattedDebouncedSearch)
      );
    });
  }, [debouncedSearch]);

  return (
    <VStack className='min-h-0 flex-1 gap-4'>
      <Input
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        rightElement={<BiSearch />}
        placeholder='Search your country'
      />
      {filteredCountryCodes.length === 0 && (
        <Text color='gray-600'>No countries found.</Text>
      )}
      <div className='max-h-[75vh] overflow-y-auto md:max-h-[50vh]'>
        <RadioGroup
          className='gap-2 pb-8'
          defaultValue={countryCode}
          onValueChange={(countryCode: TCountryCode | 'global') =>
            setCountryCode(countryCode)
          }
        >
          {[...filteredCountryCodes].map((countryCode, ix) => (
            <CountryItem
              countryCode={countryCode as TCountryCode | 'global'}
              key={ix}
              tabIndex={ix}
            />
          ))}
        </RadioGroup>
      </div>
    </VStack>
  );
};

export const CountrySelectorDialog = () => {
  return (
    <Dialog>
      <DialogTrigger>
        <CountrySelectorTrigger />
      </DialogTrigger>
      <DialogContent className='flex min-h-0 w-screen max-w-[100dvw] flex-col rounded-none border-none sm:h-auto sm:max-h-[75%] sm:max-w-lg sm:rounded-[10px] sm:border-neutral-200'>
        <DialogHeader>
          <DialogTitle>
            <Text font='medium-md'>Choose your country</Text>
          </DialogTitle>
          <DialogDescription>
            <Text font='sm'>Some products are country-specific</Text>
          </DialogDescription>
        </DialogHeader>
        <CountrySelectorContent />
      </DialogContent>
    </Dialog>
  );
};

export const CountrySelectorPage = () => {
  return (
    <VStack className='w-full gap-4'>
      <VStack className='items-center gap-2'>
        <Text font='medium-md'>Choose your country</Text>
        <Text font='sm'>Some products are country-specific</Text>
      </VStack>
      <CountrySelectorContent />
    </VStack>
  );
};

export const CountrySelector = () => {
  const { slideLeftFullPage } = useViewTransition();
  return (
    <div>
      <div className='hidden md:flex'>
        <CountrySelectorDialog />
      </div>
      <Link
        to={country_select_route.to}
        viewTransition={slideLeftFullPage}
        className='flex md:hidden'
      >
        <CountrySelectorTrigger />
      </Link>
    </div>
  );
};
